import { TableName } from '../../../../../tables'
import { Action, ModalId, UIPermission, useUIActions, DetailPanel, DetailFieldProps, Tab, FieldType, useGetCrumbs, PDFTabContent, FieldValue, SpinnerDiv } from '../../../../../features/ui'
import { Budget } from '../../../state/types'
import { PriceBreakdown, OperationItemsTable, usePriceOutlineHooks, operationHelpers } from '../../../../operations'
import { useSystemStateHooks } from '../../../../system'
import { BudgetPDF } from '../budget-pdf/budgetPDF'
import { DownloadBudgetPDFModal } from '../download-budget-pdf-modal/downloadBudgetPDFModal'
import { BudgetPriceList } from '../budget-price-list/budgetPriceList'
import { RoutePath, useRoutesHooks } from '../../../../../features/routes'
import { useNavigate } from 'react-router-dom'
import { useDescribeHooks } from '../../../../../features/data-types'
import { useEmployeeHooks, useEmployeeLoaders } from '../../../../administration'
import { useMemo } from 'react'
import { CreateOperationFromBudgetModal } from '../create-operation-from-budget-modal/createOperationFromBudgetModal'
import { useBudgetHooks } from '../../../hooks/budgetHooks'

export const BudgetDetail = () => {
  const { dataType } = useRoutesHooks().parseUrl()
  const budget = dataType! as Budget
  const { items, ...data } = budget

  const { describeCode } = operationHelpers
  const { userHasPermission } = useSystemStateHooks()
  const { getPriceOutline } = usePriceOutlineHooks()
  const { describe } = useDescribeHooks()
  const { describeEmployee } = useEmployeeHooks()
  const { getBudgetPricesList } = useBudgetHooks()
  const navigate = useNavigate()

  const employeeLoaders = useEmployeeLoaders()

  const { openModal } = useUIActions()

  const readOnly = !userHasPermission(UIPermission.SAVE_BUDGETS) || budget.archived

  const title = describe(TableName.BUDGETS, budget)
  const crumbs = useGetCrumbs(
    'Presupuestos',
    RoutePath.BUDGETS,
    describeCode(budget)
  )

  const priceOutline = useMemo(() => getPriceOutline(TableName.BUDGETS, budget), [budget])

  const fields: DetailFieldProps[] = [
    { name: 'date', type: FieldType.DATE, label: 'Fecha' },
    {
      name: 'sellerId',
      type: FieldType.TABLE,
      table: TableName.EMPLOYEES,
      label: 'Vendedor',
      render: (value?: FieldValue) => {
        const seller = employeeLoaders().findEmployee(value! as string)!
        return <>{describeEmployee(seller)}</>
      }
    },
    {
      name: 'customerId',
      type: FieldType.TABLE,
      table: TableName.CUSTOMERS,
      label: 'Cliente'
    },
    {
      name: 'customerTypeId',
      type: FieldType.TABLE,
      table: TableName.CUSTOMER_TYPES,
      label: 'Lista de Precios'
    },
    {
      name: 'budgetItems',
      type: FieldType.TABLE,
      loading: items.length === 0,
      render: () => (
        <OperationItemsTable
          itemsData={items}
          createMode={false}
          readOnly
        />
      )
    },
    {
      name: 'priceOutline',
      type: FieldType.PRICE,
      label: 'Precio',
      render: () => <PriceBreakdown data={priceOutline} />
    }
  ]

  const renderPricesContent = priceOutline.total !== 0 ?
    <BudgetPriceList priceItems={getBudgetPricesList(priceOutline.total)} /> :
    <SpinnerDiv />

  const renderPDFContent = (
    <PDFTabContent>
      <BudgetPDF
        id="budget-pdf-preview"
        budget={budget}
        previewMode
      />
    </PDFTabContent>
  )

  const tabs: Tab[] = [
    {
      id: 'payment-type-prices',
      title: 'Medios de Pago',
      content: renderPricesContent
    },
    {
      id: 'document',
      title: 'Documento',
      content: renderPDFContent
    }
  ]

  const onDownloadPDF = () => openModal(ModalId.DOWNLOAD_BUDGET_PDF)

  const onRefresh = () => navigate(RoutePath.UPDATE)

  const onCreateOperation = () => openModal(ModalId.CREATE_OPERATION_FROM_BUDGET)

  const onRemove = () => openModal(ModalId.REMOVE)

  const actions: Action[] = [
    { label: 'Descargar', kind: 'tertiary', icon: 'download', onClick: onDownloadPDF }
  ]
  if (!readOnly) {
    actions.push(
      { label: 'Actualizar', icon: 'arrow-repeat', onClick: onRefresh },
      { label: 'Crear Venta/Acopio', icon: 'tags-fill', onClick: onCreateOperation },
      { label: 'Eliminar', kind: 'secondary', icon: 'trash-fill', onClick: onRemove }
    )
  }

  return (<>
    <DetailPanel
      tableName={TableName.BUDGETS}
      title={title}
      crumbs={crumbs}
      fields={fields}
      data={data}
      icon='calculator'
      tabs={tabs}
      imgSpace={3}
      readOnly={readOnly}
      actions={actions}
    />
    <DownloadBudgetPDFModal budget={budget} />
    <CreateOperationFromBudgetModal />
  </>)
}
